import React, { useState, useCallback, useRef } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

interface StarData {
  a: number;   // semi-major axis
  ecc: number; // eccentricity
  period: number;
  color: string;
  radius: number;
  name: string;
}

export function BlackHoleOrbitTracker() {
  const [reveal, setReveal] = useState<boolean>(false);

  // Buffer to track trails for each star
  const trailsRef = useRef<{ x: number; y: number }[][]>([[], [], []]);

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const width = ctx.canvas.clientWidth;
    const height = ctx.canvas.clientHeight;

    const cx = width / 2;
    const cy = height / 2 - 25;

    // Clear background to #0F172A
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    // 3 Stars definitions
    // 3 Stars definitions matching the specification:
    // Star A: semi-major axis 80px, eccentricity 0.5, period 6s, color #FCD34D, radius 7px
    // Star B: semi-major axis 120px, eccentricity 0.3, period 10s, color #F9A8D4, radius 5px
    // Star C: semi-major axis 55px, eccentricity 0.6, period 3.5s, color #6EE7B7, radius 9px
    const stars: StarData[] = [
      { name: 'Star A', a: 80, ecc: 0.5, period: 6.0, color: '#FCD34D', radius: 7 },
      { name: 'Star B', a: 120, ecc: 0.3, period: 10.0, color: '#F9A8D4', radius: 5 },
      { name: 'Star C', a: 55, ecc: 0.6, period: 3.5, color: '#6EE7B7', radius: 9 }
    ];

    // Kepler solver
    const solveKepler = (M: number, ecc: number) => {
      let E = M;
      for (let i = 0; i < 5; i++) {
        E = E - (E - ecc * Math.sin(E) - M) / (1 - ecc * Math.cos(E));
      }
      return E;
    };

    const drawStarShape = (c: CanvasRenderingContext2D, x: number, y: number, spikes: number, outerRadius: number, innerRadius: number) => {
      let rot = (Math.PI / 2) * 3;
      let px = x;
      let py = y;
      const step = Math.PI / spikes;

      c.beginPath();
      c.moveTo(x, y - outerRadius);
      for (let i = 0; i < spikes; i++) {
        px = x + Math.cos(rot) * outerRadius;
        py = y + Math.sin(rot) * outerRadius;
        c.lineTo(px, py);
        rot += step;

        px = x + Math.cos(rot) * innerRadius;
        py = y + Math.sin(rot) * innerRadius;
        c.lineTo(px, py);
        rot += step;
      }
      c.lineTo(x, y - outerRadius);
      c.closePath();
    };

    stars.forEach((star, idx) => {
      const b = star.a * Math.sqrt(1.0 - star.ecc * star.ecc);

      // 1. DRAW DASHED ORBIT ELLIPSE
      ctx.save();
      // Translate to focus center (focus is at cx, cy, so ellipse center is at cx - a*ecc, cy)
      ctx.translate(cx - star.a * star.ecc, cy);
      ctx.scale(1, b / star.a);
      
      ctx.strokeStyle = star.color;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.2; // 0.2 opacity strokeStyle
      ctx.setLineDash([4, 4]);
      
      ctx.beginPath();
      ctx.arc(0, 0, star.a, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1.0;

      // 2. CALCULATE POSITION & VELOCITY TANGENT
      const n = (2.0 * Math.PI) / star.period;
      const M = n * totalTime;
      const E = solveKepler(M, star.ecc);

      // Coordinates relative to focus (singularity at cx,cy)
      const x = star.a * (Math.cos(E) - star.ecc);
      const y = b * Math.sin(E);
      
      const starX = cx + x;
      const starY = cy + y;

      // Velocity components (Keplerian)
      const den = 1.0 - star.ecc * Math.cos(E);
      const vx = (-star.a * Math.sin(E) * n) / den;
      const vy = (b * Math.cos(E) * n) / den;

      // Store in trail buffer (trail of last 60 positions)
      const trail = trailsRef.current[idx];
      trail.push({ x: starX, y: starY });
      if (trail.length > 60) {
        trail.shift();
      }

      // 3. DRAW TRAILS
      trail.forEach((pos, tIdx) => {
        const opacity = (tIdx / trail.length) * 0.35;
        ctx.fillStyle = star.color;
        ctx.globalAlpha = opacity;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, star.radius * 0.45, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1.0;

      // 4. DRAW VELOCITY VECTOR ARROW
      // Direction = tangent to orbit, length proportional to orbital speed, base 5px.
      const speed = Math.sqrt(vx * vx + vy * vy);
      const dx = vx / speed;
      const dy = vy / speed;
      const arrowLen = 5 + speed * 0.15;
      const arrowX = starX + dx * arrowLen;
      const arrowY = starY + dy * arrowLen;

      ctx.strokeStyle = star.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(starX, starY);
      ctx.lineTo(arrowX, arrowY);
      ctx.stroke();

      // Draw arrowhead
      const arrowHeadSize = 3.5;
      const angle = Math.atan2(vy, vx);
      ctx.fillStyle = star.color;
      ctx.beginPath();
      ctx.moveTo(arrowX, arrowY);
      ctx.lineTo(
        arrowX - arrowHeadSize * Math.cos(angle - Math.PI / 6),
        arrowY - arrowHeadSize * Math.sin(angle - Math.PI / 6)
      );
      ctx.lineTo(
        arrowX - arrowHeadSize * Math.cos(angle + Math.PI / 6),
        arrowY - arrowHeadSize * Math.sin(angle + Math.PI / 6)
      );
      ctx.closePath();
      ctx.fill();

      // 5. DRAW STAR SPHERE
      // Star Glow
      const glow = ctx.createRadialGradient(starX, starY, 0, starX, starY, star.radius * 2.2);
      glow.addColorStop(0, star.color);
      glow.addColorStop(1, 'rgba(15, 23, 42, 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(starX, starY, star.radius * 2.2, 0, Math.PI * 2);
      ctx.fill();

      // Star core
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(starX, starY, star.radius * 0.7, 0, Math.PI * 2);
      ctx.fill();

      // Star label tag next to it
      ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.font = '8px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(star.name, starX + star.radius + 4, starY + 3);
    });

    // 6. RENDER REVEALED BLACK HOLE MASS CENTER
    if (reveal) {
      // Small red crosshair (+)
      ctx.strokeStyle = 'rgba(239, 68, 68, 0.8)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(cx - 10, cy);
      ctx.lineTo(cx + 10, cy);
      ctx.moveTo(cx, cy - 10);
      ctx.lineTo(cx, cy + 10);
      ctx.stroke();

      // Pulsing red ★ at canvas centre
      const pulse = 1.0 + Math.sin(totalTime * 6.0) * 0.2;
      ctx.save();
      ctx.shadowColor = '#EF4444';
      ctx.shadowBlur = 12;
      ctx.fillStyle = '#EF4444';
      drawStarShape(ctx, cx, cy, 5, 8 * pulse, 3.5 * pulse);
      ctx.fill();
      ctx.restore();
    }

    // Top-Left Headers
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Stellar Orbit Tracker (Sgr A*)', 16, 26);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.font = '10px sans-serif';
    ctx.fillText('Three stars orbit around an invisible gravitational center', 16, 42);
  }, [reveal]);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas ref={canvasRef} className="block w-full h-full" />

      {/* Info panel when revealed */}
      {reveal && (
        <div className="absolute top-3 right-3 w-[220px] bg-[#0b0f19]/90 backdrop-blur-md p-3 rounded-lg border border-red-500/20 shadow-xl flex flex-col gap-1.5 animation-fade-in text-white animate-fade-in">
          <span className="font-bold text-xs text-red-400">Black Hole Detected!</span>
          <div className="flex flex-col gap-0.5">
            <span className="text-[9px] uppercase tracking-wider text-white/40">Calculated Black Hole Mass</span>
            <span className="text-xs font-bold">~4 Million M☉</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[9px] uppercase tracking-wider text-white/40">Method</span>
            <p className="text-[10px] leading-tight text-white/70">
              Tracking stellar orbits around empty space
            </p>
          </div>
        </div>
      )}

      {/* Button overlay bottom center */}
      <div className="absolute bottom-3 inset-x-0 flex justify-center">
        <button
          onClick={() => setReveal(!reveal)}
          className={`px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider transition-all duration-300 border ${
            reveal
              ? 'bg-red-500 hover:bg-red-600 border-red-500 text-white shadow-lg shadow-red-500/20'
              : 'bg-white/10 hover:bg-white/20 border-white/10 text-white'
          }`}
        >
          {reveal ? 'Hide Mass Center' : 'Reveal Invisible Mass'}
        </button>
      </div>
    </div>
  );
}
